import React from "react";
import { useTheme } from "src/context/ThemeContext";

interface TablePaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  itemName?: string;
  loading?: boolean;
  className?: string;
  "data-testid"?: string;
}

function TablePagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemName = "items",
  loading = false,
  className = "",
  "data-testid": dataTestId = "pagination-container",
}: TablePaginationProps) {
  const { theme } = useTheme();

  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    // Only allow page changes if there are items or if going to page 1
    if (totalItems > 0 || page === 1) {
      onPageChange(page);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const showEllipsis = totalPages > 7;

    // Always show at least page 1, even if there's no data
    if (totalPages === 0) {
      pages.push(1);
    } else if (!showEllipsis) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 4) {
        pages.push("...");
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 3) {
        pages.push("...");
      }

      // Always show last page if more than 1 page
      if (totalPages > 1 && !pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  // Theme-aware styles
  const containerClasses = `tw-flex tw-items-center tw-justify-between tw-border-t tw-px-4 tw-py-3 sm:tw-px-6 ${
    theme === "light"
      ? "tw-border-gray-200 tw-bg-white"
      : "tw-border-slate-600 tw-bg-slate-800"
  } ${className}`;

  const textClasses =
    theme === "light" ? "tw-text-gray-700" : "tw-text-slate-300";

  const selectClasses = `tw-form-select tw-rounded-md tw-border tw-text-sm tw-shadow-sm tw-w-[70px] tw-px-3 tw-py-2 ${
    theme === "light"
      ? "tw-border-gray-300 tw-bg-white tw-text-gray-700 focus:tw-border-primary focus:tw-ring-primary/50"
      : "tw-border-slate-600 tw-bg-slate-700 tw-text-slate-200 focus:tw-border-primary focus:tw-ring-primary/50"
  }`;

  const buttonBaseClasses =
    "tw-relative tw-inline-flex tw-items-center tw-rounded-md tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition-colors";

  const buttonEnabledClasses =
    theme === "light"
      ? "tw-border-gray-300 tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50"
      : "tw-border-slate-600 tw-bg-slate-700 tw-text-slate-300 hover:tw-bg-slate-600";

  const buttonDisabledClasses =
    "tw-border-gray-200 tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed tw-opacity-50";

  const pageButtonClasses = (isActive: boolean, isDisabled: boolean) => {
    const base =
      "tw-relative tw-inline-flex tw-items-center tw-border-y tw-border-r tw-px-4 tw-py-2 tw-text-sm tw-font-medium";

    if (isActive) {
      return `${base} tw-z-10 tw-border-primary tw-bg-primary/10 tw-text-primary`;
    }

    if (isDisabled) {
      return `${base} tw-border-gray-300 tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed`;
    }

    return `${base} ${
      theme === "light"
        ? "tw-border-gray-300 tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50"
        : "tw-border-slate-600 tw-bg-slate-700 tw-text-slate-300 hover:tw-bg-slate-600"
    }`;
  };

  return (
    <nav
      aria-label="Pagination"
      className={containerClasses}
      data-testid={dataTestId}
    >
      {/* Results info - hidden on mobile */}
      <div className="tw-hidden sm:tw-block">
        {loading ? (
          <p className={`tw-text-sm ${textClasses}`}>Loading {itemName}...</p>
        ) : totalItems > 0 ? (
          <p className={`tw-text-sm ${textClasses}`}>
            Showing <span className="tw-font-medium">{startItem}</span> to{" "}
            <span className="tw-font-medium">{endItem}</span> of{" "}
            <span className="tw-font-medium">{totalItems}</span> {itemName}
          </p>
        ) : (
          <p className={`tw-text-sm ${textClasses}`}>No {itemName} found</p>
        )}
      </div>

      {/* Pagination controls */}
      <div className="tw-flex tw-flex-1 tw-items-center tw-justify-between sm:tw-justify-end tw-gap-4">
        {/* Page size selector */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <p className={`tw-text-sm ${textClasses} tw-whitespace-nowrap`}>
            Rows per page:
          </p>
          <select
            data-testid="page-size-select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className={selectClasses}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Page navigation */}
        <div className="tw-flex tw-items-center tw-gap-2">
          {/* Previous button */}
          <button
            data-testid="prev-button"
            onClick={handlePrevious}
            disabled={currentPage <= 1}
            className={`${buttonBaseClasses} ${
              currentPage <= 1 ? buttonDisabledClasses : buttonEnabledClasses
            }`}
          >
            Previous
          </button>

          {/* Page numbers - hidden on mobile */}
          <div className="tw-hidden md:tw-flex tw-items-center">
            {pageNumbers.map((page, index) => {
              if (page === "...") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className={`tw-relative tw-inline-flex tw-items-center tw-border-y tw-border-r tw-px-4 tw-py-2 tw-text-sm tw-font-medium ${
                      theme === "light"
                        ? "tw-border-gray-300 tw-bg-white tw-text-gray-700"
                        : "tw-border-slate-600 tw-bg-slate-700 tw-text-slate-300"
                    }`}
                  >
                    ...
                  </span>
                );
              }

              const pageNum = page as number;
              const isActive = pageNum === currentPage;
              const isFirst = pageNum === 1;
              const isDisabled = totalItems === 0 && pageNum !== 1;

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageClick(pageNum)}
                  disabled={isDisabled}
                  className={`${pageButtonClasses(isActive, isDisabled)} ${
                    isFirst ? "tw-border-l" : ""
                  }`}
                  data-testid={`page-button-${pageNum}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          {/* Next button */}
          <button
            data-testid="next-button"
            onClick={handleNext}
            disabled={currentPage >= totalPages}
            className={`${buttonBaseClasses} ${
              currentPage >= totalPages
                ? buttonDisabledClasses
                : buttonEnabledClasses
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </nav>
  );
}

export default TablePagination;
export type { TablePaginationProps };
